/**
 * Icon Registry System
 * 
 * Dynamically imports all SVG icons from the svgs directory using Vite's import.meta.glob.
 * Icons are automatically discovered and registered by filename (without extension).
 * 
 * Usage:
 *   import { getIcon } from '~/design-system/icons/icon-registry';
 *   const svgContent = getIcon('arrow-right');
 */

// Import all SVG files as raw strings using Vite's import.meta.glob
const iconModules = import.meta.glob('./svgs/*.svg', {
  eager: true,
  query: '?raw',
  import: 'default',
});

// Colorful / brand marks live under `svgs/Brand/` — register as `brand-{lowercase-stem}`
const brandIconModules = import.meta.glob('./svgs/Brand/*.svg', {
  eager: true,
  query: '?raw',
  import: 'default',
});

// Create registry mapping icon names to SVG content
const iconRegistry: Record<string, string> = {};

// Populate registry with icon name -> SVG content mapping
for (const path in iconModules) {
  // Extract icon name from path: './svgs/arrow-right.svg' -> 'arrow-right'
  const name = path.replace('./svgs/', '').replace('.svg', '');
  iconRegistry[name] = iconModules[path] as string;
}

for (const path in brandIconModules) {
  const stem = path.replace('./svgs/Brand/', '').replace(/\.svg$/i, '');
  const name = `brand-${stem.toLowerCase()}`;
  iconRegistry[name] = brandIconModules[path] as string;
}

/**
 * Get SVG content for an icon by name
 * @param name - Icon name (without .svg extension)
 * @returns SVG content string or undefined if icon not found
 */
export function getIcon(name: string): string | undefined {
  return iconRegistry[name];
}

/**
 * Get all available icon names
 * @returns Array of icon names
 */
export function getAvailableIcons(): string[] {
  return Object.keys(iconRegistry);
}

/**
 * Check if an icon exists in the registry
 * @param name - Icon name to check
 * @returns True if icon exists, false otherwise
 */
export function hasIcon(name: string): boolean {
  return name in iconRegistry;
}

// Export the registry for direct access if needed
export { iconRegistry };

// Export type for icon names (will be union of available icon names)
export type IconName = keyof typeof iconRegistry;

